/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, April 2020
 */

import { Question } from '@staffino/model';
import {
  Box,
  convertCssUnit,
  convertLineHeight,
  Paragraph,
  Text,
  styled,
} from '@staffino/primitive-controls';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { space } from 'styled-system';
import type { MarginProps } from 'styled-system';
import Tooltip from './tooltip';
import Markdown from './controls/markdown';

const HeaderBox = styled(Box)(space);

const MarkdownContainer = styled(Paragraph)({
  '& > p': {
    margin: 0,
  },
});

type Props = {
  question: Partial<Question>;
  size: Question['extraParams']['titleSize'];
  answerErrorMessage?: string;
  answerErrorMessageType?: 'error' | 'info';
  required?: boolean;
} & React.ComponentProps<typeof HeaderBox> &
  MarginProps;

function Header({
  question,
  size,
  answerErrorMessage,
  answerErrorMessageType,
  required,
  ...rest
}: Props) {
  const { t } = useTranslation();
  const fontSize = convertCssUnit(
    size === 'small' ? '1.25rem' : ['1.5rem', '2rem'],
  );
  const lineHeight = convertLineHeight(1.2, fontSize);
  return (
    <HeaderBox {...rest}>
      <Tooltip
        tooltip={t('This question needs to be answered.')}
        show={required && !!question.title}
      >
        <MarkdownContainer
          flexDirection="row"
          justifyContent="center"
          textAlign="center"
          fontSize={fontSize}
          fontWeight="bold"
          color="headline"
          mb={2}
          mx="auto"
          lineHeight={lineHeight}
          maxWidth={convertCssUnit('28.75rem')}
          alignSelf="stretch"
          // width="100%"
        >
          <Markdown markdown={question.title} />
          {required && !!question.title && (
            <Text fontSize={fontSize} lineHeight={lineHeight} color="#a3b2bf">
              *
            </Text>
          )}
        </MarkdownContainer>

        {required && !question.title && !answerErrorMessage && (
          <Paragraph textAlign="center" color="lightText">
            <Trans>Required</Trans>
          </Paragraph>
        )}

        {!!answerErrorMessage && (
          <Paragraph
            color={
              answerErrorMessageType === 'info' ? undefined : 'notification'
            }
            flexDirection={'row' as const}
            justifyContent="center"
            alignItems="center"
            textAlign="center"
            mb={convertCssUnit('0.625rem')}
          >
            {answerErrorMessage}
          </Paragraph>
        )}
      </Tooltip>

      {question.description && (
        <Paragraph textAlign="center" color="headline" mb={2}>
          <Markdown markdown={question.description} />
        </Paragraph>
      )}
    </HeaderBox>
  );
}

export default Header;
